import React from 'react';
import {
  AppBar,
  Box,
  Dialog,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Toolbar,
} from '@material-ui/core';
import MenuIcon from '@material-ui/icons/Menu';
import CloseIcon from '@material-ui/icons/Close';
import {
  createStyles,
  makeStyles,
  Theme,
  useTheme,
} from '@material-ui/core/styles';
import { Link as RouterLink } from 'react-router-dom';

import { Button } from '@bizapp-lp/pjr/molecules/Button';

const useStylesList = makeStyles((theme: Theme) =>
  createStyles({
    links: {
      color: '#636363',
      fontSize: '14px',
      letterSpacing: 0,
      display: 'none',
      [theme.breakpoints.up('sm')]: {
        display: 'inline-flex',
        alignItems: 'center',
        marginRight: 0,
        marginLeft: theme.spacing(3),
      },
      [theme.breakpoints.up(768)]: {
        marginRight: theme.spacing(3),
        marginLeft: 0,
      },
      '&:link': { color: '#333333' },
      '&:visited': { color: '#333333' },
      '&:action': {
        color: '#333333',
        textDecoration: 'underline',
      },
      '&:focus': {
        color: '#333333',
        textDecoration: 'underline',
      },
      '&:hover': {
        color: '#333333',
        textDecoration: 'underline',
      },
    },
    trialButton: {
      width: '170px',
      height: '32px',
      display: 'none',
      [theme.breakpoints.up(768)]: {
        display: 'inline',
      },
    },
  }),
);

export interface LinkProps {
  readonly url: string;
  readonly label: string;
}

interface NavigationProps {
  readonly links: LinkProps[];
  readonly buttonLabel: string;
  readonly onClick?: React.MouseEventHandler<HTMLButtonElement>;
}

const NavigationList: React.FC<NavigationProps> = ({
  links = [],
  buttonLabel = '30日間無料トライアル',
  onClick = () => {},
}) => {
  const classes = useStylesList(useTheme());

  return (
    <Box component="nav">
      {links.map((v, i) => {
        return (
          <Link
            component={RouterLink}
            to={v.url}
            key={`header-link-${i}`}
            className={classes.links}
          >
            {v.label}
          </Link>
        );
      })}
      <Button
        variant="primary"
        shallow
        smallLabel
        label={buttonLabel}
        onClick={onClick}
        className={classes.trialButton}
      />
    </Box>
  );
};

const useStylesDrawer = makeStyles((theme: Theme) => ({
  menuButton: {
    color: '#7F7F7F',
  },
  dialog: {
    '& .MuiPaper-root': {
      position: 'absolute',
      top: 0,
      margin: 0,
      width: '100%',
      maxWidth: '100%',
      borderRadius: 0,
    },
  },
  dialogLinkList: {
    padding: theme.spacing(1.5, 0, 1),
    '& .MuiListItem-root': {
      padding: theme.spacing(6 / 8, 2, 5 / 8),
      '&:hover': {
        backgroundColor: '#EDEFF3',
      },
    },
    '& .MuiTypography-root': {
      textAlign: 'left',
      fontSize: '14px',
      color: '#636363',
    },
  },
  dialogButton: {
    width: 'calc(100% - 32px)',
    maxWidth: '343px',
    height: '32px',
    margin: theme.spacing(0, 'auto', 2.5),
  },
}));

const NavigationDrawer: React.FC<NavigationProps> = ({
  links,
  buttonLabel,
  onClick = () => {},
}) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const classesDrawer = useStylesDrawer(useTheme());

  const [open, setOpen] = React.useState(false);
  const handleToggle = () => {
    setOpen((prev) => !prev);
  };
  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <IconButton
        aria-label="open menu"
        edge="end"
        onClick={handleToggle}
        className={classesDrawer.menuButton}
      >
        {open ? <CloseIcon /> : <MenuIcon />}
      </IconButton>
      <Dialog
        open={open}
        onClose={handleClose}
        style={{ zIndex: theme.zIndex.appBar - 1 }}
        className={classesDrawer.dialog}
      >
        <Toolbar className={classes.bgToolbar} />
        <List className={classesDrawer.dialogLinkList}>
          {links.map((v, i) => {
            return (
              <ListItem
                button
                component={RouterLink}
                to={v.url}
                key={`header-link-${i}`}
              >
                <ListItemText>{v.label}</ListItemText>
              </ListItem>
            );
          })}
        </List>
        <Button
          variant="primary"
          shallow
          smallLabel
          label={buttonLabel}
          onClick={onClick}
          className={classesDrawer.dialogButton}
        />
      </Dialog>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      backgroundColor: '#FFFFFF',
      boxShadow: '0px 5px 10px #00000014',
      padding: theme.spacing(0, 1, 0, 0),
      [theme.breakpoints.up('tablet')]: {
        paddingRight: theme.spacing(4),
        paddingLeft: theme.spacing(2),
      },
    },
    bgToolbar: { minHeight: '100px' },
    toolbar: {
      display: 'inline-block',
      minHeight: '100px',
      alignItems: 'center',
    },
    logobox: { },
    logo: {
      width: '174px',
    },
  }),
);

export interface HeaderProps extends Partial<NavigationProps> {
  readonly showLinks?: boolean;
}

const HeaderComponent: React.FC<HeaderProps> = ({
  showLinks = false,
  ...navProps
}) => {
  const classes = useStyles(useTheme());

  const completeNavProps: NavigationProps = {
    links: [],
    buttonLabel: '資料ダウンロード',
    onClick: () => {},
    ...navProps,
  };

  const [width, setWidth] = React.useState(window.innerWidth);
  React.useEffect(() => {
    const updateSize = () => {
      setWidth(window.innerWidth);
    };
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  /**
   * Dealed with a rendering problem that a top of content is covered with Header.
   *
   * See also: https://material-ui.com/components/app-bar/#fixed-placement
   */
  return (
    <>
      <AppBar position="fixed" className={classes.root}>
        <Toolbar className={classes.toolbar} disableGutters={true}>
          <Box flexGrow={1} className={classes.logobox}>
            <Link href="https://www.worksap.co.jp/saas/projectroom/">
              <img
                className={classes.logo}
                src={`${process.env.PUBLIC_URL}/images/wap_logo_rgb_positive.png`}
                alt={'WAP-logo'}
              />
            </Link>
          </Box>
          {showLinks &&
            (width >= 768 ? (
              <NavigationList {...completeNavProps} />
            ) : (
              <NavigationDrawer {...completeNavProps} />
            ))}
        </Toolbar>
      </AppBar>
      <Toolbar className={classes.bgToolbar} />
    </>
  );
};

export const Header = React.memo(HeaderComponent);
